import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { activityApi, usersApi, companiesApi } from "../services/api";
import {
  ACTIVITY_LOG_PAGE_SIZE,
  canLoadMoreActivity,
  hitActivityLogCeiling,
  nextActivityLogLimit,
} from "../utils/activityLogPaging";

/**
 * The system-log vocabulary: every `ActivityLog.action` and `ActivityLog.entity_type` token the
 * backend can write, and its Hebrew name.
 *
 * These maps are exported because `ActivityLogPage.vocabulary.test.jsx` reads the backend producers
 * and fails if either drifts from them. That control exists because all three maps below were short
 * of what the log can carry. `ACTION_LABEL` had no name for the project-note pair, the
 * customer-document upload, the two knowledge-gate observations, the Acronis tenant discovery and its
 * three mapping events, or the AI-Factory `create`/`update`; `ENTITY_LABEL`/`ENTITY_ICON` had none for
 * `agent_run`, `customer_document`, `factory_task` or `factory_worker`. Most of those reach the row
 * through one of the backend's three forwarding wrappers rather than a literal `log_activity(` call,
 * which is why no earlier sweep found them. Every one rendered through `map[token] ?? token`, i.e. as
 * the producer's own English identifier on a Hebrew audit screen.
 *
 * A token with no producer is harmless here (the backend once wrote several this build no longer
 * mints), so the maps stay supersets. An unmapped token still prints itself: showing the operator the
 * identifier the producer wrote is the honest failure, and inventing a Hebrew word for an action the
 * vocabulary does not describe would be a guess presented as a label.
 */
export const ACTION_LABEL = {
  created:        "יצר/ה",
  status_changed: "שינה/תה סטטוס",
  assigned:       "שיבץ/ה",
  updated:        "עדכן/ה",
  deleted:        "מחק/ה",
  merged:         "מיזג/ה",
  split:          "פיצל/ה",
  note_added:     "הוסיף/ה הערה",
  queue_changed:  "העביר/ה לתור",
  agent_permission_granted: "העניק/ה הרשאת סוכן",
  agent_permission_revoked: "שלל/ה הרשאת סוכן",
  technician_created: "הוסיף/ה טכנאי",
  technician_updated: "עדכן/ה טכנאי",
  technician_deleted: "מחק/ה טכנאי והסיר/ה גישה",
  company_created: "יצר/ה חברה",
  company_updated: "עדכן/ה חברה",
  company_deleted: "מחק/ה חברה",
  knowledge_article_created: "יצר/ה מאמר ידע",
  knowledge_article_updated: "עדכן/ה מאמר ידע",
  knowledge_article_approved: "אישר/ה מאמר ידע",
  knowledge_article_deleted: "מחק/ה מאמר ידע",
  customer_profile_created: "יצר/ה סביבת IT ללקוח",
  customer_profile_updated: "עדכן/ה סביבת IT של לקוח",
  scheduled_check_created: "יצר/ה בדיקה מתוזמנת",
  scheduled_check_updated: "עדכן/ה בדיקה מתוזמנת",
  scheduled_check_deleted: "מחק/ה בדיקה מתוזמנת",
  scheduled_check_run_now: "הפעיל/ה בדיקה מתוזמנת ידנית",
  agent_created: "יצר/ה סוכן",
  agent_updated: "עדכן/ה הגדרת סוכן",
  agent_disabled: "השבית/ה סוכן",
  agent_connection_added: "הוסיף/ה חיבור לסוכן",
  agent_connection_removed: "הסיר/ה חיבור מסוכן",
  agent_connection_tested: "בדק/ה חיבור של סוכן",
  // Guarded Connection publish (PROD-BUILDER-058). The first token is the durable rejection of a
  // system the Builder could not recognize; the second is a replay of that exact reviewed request,
  // refused before any connection is written. Both are operator-visible on the employee's own row.
  agent_connection_proposal_rejected: "דחה/תה מערכת לא נתמכת",
  agent_connection_publish_refused: "חסמ/ה ניסיון חוזר לחיבור שנדחה",
  // Builder draft consistency (PROD-BUILDER-059). The draft save is one recorded outcome with
  // published/proposed/refused items; the idempotent-create ledger row records which employee a
  // repeated create attempt answered with. Both are ordinary activity rows.
  agent_builder_draft_saved: "שמר/ה טיוטת עובד",
  agent_create_idempotent: "זיהה/תה ניסיון יצירה חוזר",
  api_token_created: "יצר/ה טוקן API",
  api_token_revoked: "ביטל/ה טוקן API",
  crisis_mode_activated: "הפעיל/ה מצב חירום",
  crisis_mode_deactivated: "כיבה/תה מצב חירום",
  // The vocabulary read resolves the Builder's `log_activity(..., action, ...)` variable over the
  // whole module, so it also sees the `action=`/`outcome=` keywords of the crisis-mode SecurityEvent
  // calls beside it. Those are not ActivityLog actions, but the maps stay supersets and the names
  // below are the same acts already named above, so no token can ever render as an identifier.
  crisis_mode_activate: "הפעיל/ה מצב חירום",
  crisis_mode_deactivate: "כיבה/תה מצב חירום",
  GRANTED: "אושר/ה",
  ai_auto_trigger_enabled: "הפעיל/ה הפעלה אוטומטית של AI",
  ai_auto_trigger_disabled: "כיבה/תה הפעלה אוטומטית של AI",
  knowledge_gap_status_changed: "שינה/תה סטטוס פער ידע",
  security_finding_status_changed: "שינה/תה סטטוס ממצא אבטחה",
  tech_discovery_added: "הוסיף/ה גילוי טכנולוגי",
  tech_discovery_decided: "קבע/ה החלטה על גילוי טכנולוגי",
  managed_domain_created: "הוסיף/ה דומיין מנוהל",
  managed_domain_updated: "עדכן/ה דומיין מנוהל",
  managed_domain_deleted: "מחק/ה דומיין מנוהל",
  milestone_deleted: "מחק/ה אבן דרך",
  task_deleted: "מחק/ה משימה",
  ai_pilot_enabled: "הפעיל/ה את AI Technician",
  ai_pilot_disabled: "כיבה/תה את AI Technician",
  ai_feature_toggled: "שינה/תה הגדרת AI",
  ai_api_key_changed: "החליף/ה מפתח API של AI",
  alert_config_changed: "שינה/תה הגדרת התראות",
  backup_guardian_config_changed: "שינה/תה הגדרת Backup Guardian",
  tickets_exported: "ייצא/ה גיבוי קריאות",
  note_deleted: "מחק/ה הערה פנימית",
  security_finding_reopened: "פתח/ה מחדש ממצא אבטחה",
  blocklist_entry_added: "הוסיף/ה חסימת מייל",
  blocklist_entry_removed: "הסיר/ה חסימת מייל",
  blocklist_rule_added: "הוסיף/ה כלל חסימה משולב",
  blocklist_rule_removed: "הסיר/ה כלל חסימה משולב",
  agent_memory_created: "יצר/ה זיכרון לקוח",
  agent_memory_verified: "אימת/ה זיכרון לקוח",
  agent_memory_corrected: "תיקן/ה זיכרון לקוח",
  agent_memory_superseded: "החליף/ה זיכרון לקוח",
  agent_memory_deprecated: "הוציא/ה זיכרון לקוח משימוש",
  agent_memory_deleted: "מחק/ה זיכרון לקוח",
  agent_memory_purged: "טיהר/ה זיכרונות לקוח ישנים",
  // Project notes and the customer-document upload: the four tokens below reach the log through
  // producers that never had a name here, so the row read as the identifier itself.
  note_created: "הוסיף/ה הערה",
  note_updated: "עדכן/ה הערה",
  customer_document_uploaded: "העלה/תה מסמך לקוח",
  // The knowledge gate records what an AI run observed and what it went on to research, on the run
  // itself: without these two the row was the only trace of a gate decision, in English.
  knowledge_gate_observed: "רשם/ה אי-התאמה בשער הידע",
  knowledge_gate_research_recorded: "רשם/ה מקור מחקר לשער הידע",
  // Acronis tenant discovery and mapping, read and written by the agents screen.
  agent_connection_acronis_tenants_discovered: "גילה/תה דיירים ב-Acronis",
  acronis_tenant_mapping_created: "מיפה/תה דייר Acronis",
  acronis_tenant_mapping_updated: "עדכן/ה מיפוי דייר Acronis",
  acronis_tenant_mapping_removed: "הסיר/ה מיפוי דייר Acronis",
  // AI Factory task and worker edits. The words are the plain ones a Hebrew reader expects ("יצר/ה",
  // "עדכן/ה"), the same verbs the rest of this map uses for the same acts elsewhere.
  create: "יצר/ה",
  update: "עדכן/ה",
  // Governed Skill assignment (PROD-SKILL-031). Both verbs are written on the employee's own
  // activity row, so an unmapped token would leave the management trail reading in English.
  skill_assigned: "הקצה/תה מיומנות",
  skill_unassigned: "הסיר/ה שיוך מיומנות",
  // Pilot feedback capture and explicit triage (FULL-0164 / PROD-UX-078). Both rows are ordinary
  // metadata-only audit entries; the classification is an explicit Admin/Owner decision.
  pilot_feedback_recorded: "רשם/ה פידבק פיילוט",
  pilot_feedback_triaged: "סיווג/ה פידבק פיילוט",
};

// Audit actions introduced by governed onboarding, reuse, skill admission and evaluation.
Object.assign(ACTION_LABEL, {
  "provider_onboarding.provisioned": "\u05e1\u05e4\u05e7 \u05d4\u05d5\u05d2\u05d3\u05e8",
  "provider_onboarding.activated": "\u05e1\u05e4\u05e7 \u05d4\u05d5\u05e4\u05e2\u05dc",
  reuse_decision_recorded: "\u05ea\u05d5\u05e2\u05d3\u05d4 \u05d4\u05d7\u05dc\u05d8\u05ea \u05e9\u05d9\u05de\u05d5\u05e9 \u05d7\u05d5\u05d6\u05e8",
  skill_admission_recorded: "\u05ea\u05d5\u05e2\u05d3\u05d4 \u05e7\u05d1\u05dc\u05ea \u05de\u05d9\u05d5\u05de\u05e0\u05d5\u05ea",
  skill_evaluation_recorded: "\u05ea\u05d5\u05e2\u05d3\u05d4 \u05d4\u05e2\u05e8\u05db\u05ea \u05de\u05d9\u05d5\u05de\u05e0\u05d5\u05ea",
  "customer_connection_profile.registered": "\u05e8\u05e9\u05dd/\u05d4 \u05e4\u05e8\u05d5\u05e4\u05d9\u05dc \u05d7\u05d9\u05d1\u05d5\u05e8 \u05dc\u05dc\u05e7\u05d5\u05d7",
  "customer_connection_profile.revoked": "\u05d1\u05d9\u05d8\u05dc/\u05d4 \u05e4\u05e8\u05d5\u05e4\u05d9\u05dc \u05d7\u05d9\u05d1\u05d5\u05e8 \u05dc\u05dc\u05e7\u05d5\u05d7",
});
export const SYSTEM_ACTION_LABEL = {
  "ticket:created": "קריאה חדשה התקבלה ממייל",
};

const ACTION_COLOR = {
  created:        { bg: "#dcfce7", text: "#166534" },
  status_changed: { bg: "#dbeafe", text: "#1e40af" },
  assigned:       { bg: "#ede9fe", text: "#5b21b6" },
  updated:        { bg: "#f3f4f6", text: "#374151" },
  deleted:        { bg: "#fee2e2", text: "#991b1b" },
  merged:         { bg: "#ffedd5", text: "#9a3412" },
  split:          { bg: "#e0f2fe", text: "#0369a1" },
  note_added:     { bg: "#ccfbf1", text: "#115e59" },
  queue_changed:  { bg: "#fef9c3", text: "#854d0e" },
};

export const ENTITY_LABEL = { ticket: "קריאה", project: "פרויקט", maintenance: "תחזוקה", agent_permission: "הרשאות סוכנים", agent_control: "בקרת סוכנים", technician: "טכנאים", api_token: "טוקני API", agent: "סוכנים", agent_run: "ריצות סוכן", company: "חברות", knowledge_article: "מאמרי ידע", customer_profile: "סביבת IT", customer_document: "מסמכי לקוח", scheduled_check: "בדיקות מתוזמנות", knowledge_gap: "פערי ידע", security_finding: "ממצאי אבטחה", tech_discovery: "גילויים טכנולוגיים", managed_domain: "דומיינים מנוהלים", contact: "אנשי קשר", backup: "גיבוי", agent_memory: "זיכרון לקוח", email_blocklist: "חסימת מיילים", factory_task: "משימות מפעל AI", factory_worker: "עובדי מפעל AI", pilot_feedback: "פידבק פיילוט" };
const ENTITY_LINK  = { ticket: "/tickets", project: "/projects", maintenance: "/maintenance" };
export const ENTITY_ICON  = { ticket: "🎫", project: "📁", maintenance: "🔧", agent_permission: "🔑", agent_control: "🛡️", technician: "👤", api_token: "🔐", agent: "🤖", agent_run: "🏃", company: "🏢", knowledge_article: "📚", customer_profile: "🖥️", customer_document: "📄", scheduled_check: "⏱️", knowledge_gap: "🕳️", security_finding: "🛡️", tech_discovery: "🔭", managed_domain: "🌐", contact: "📇", backup: "💾", agent_memory: "🧠", email_blocklist: "🚫", factory_task: "🏭", factory_worker: "🧰", pilot_feedback: "📝" };

// Explicit display names and symbols for newer audit entity types.
Object.assign(ENTITY_LABEL, {
  provider_onboarding: "\u05e7\u05dc\u05d9\u05d8\u05ea \u05e1\u05e4\u05e7",
  reuse_decision: "\u05d4\u05d7\u05dc\u05d8\u05ea \u05e9\u05d9\u05de\u05d5\u05e9 \u05d7\u05d5\u05d6\u05e8",
  skill_admission: "\u05e7\u05d1\u05dc\u05ea \u05de\u05d9\u05d5\u05de\u05e0\u05d5\u05ea",
  skill_evaluation: "\u05d4\u05e2\u05e8\u05db\u05ea \u05de\u05d9\u05d5\u05de\u05e0\u05d5\u05ea",
  customer_connection_profile: "\u05e4\u05e8\u05d5\u05e4\u05d9\u05dc \u05d7\u05d9\u05d1\u05d5\u05e8 \u05dc\u05e7\u05d5\u05d7",
});
Object.assign(ENTITY_ICON, {
  provider_onboarding: "\u{1F4E6}",
  reuse_decision: "\u267B\uFE0F",
  skill_admission: "\u2705",
  skill_evaluation: "\u{1F4CA}",
  customer_connection_profile: "\u{1F50C}",
});

function timeAgo(ts) {
  // Backend returns naive UTC strings (no Z/offset) — force UTC parsing to avoid local-tz shift
  const utc = ts && !ts.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(ts) ? ts + "Z" : ts;
  const diff = Date.now() - new Date(utc).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "לפני רגע";
  if (m < 60) return `לפני ${m} דקות`;
  const h = Math.floor(m / 60);
  if (h < 24) return `לפני ${h} שעות`;
  const d = Math.floor(h / 24);
  if (d < 30) return `לפני ${d} ימים`;
  return new Date(utc).toLocaleDateString("he-IL");
}

function ActionBadge({ action }) {
  const { bg, text } = ACTION_COLOR[action] ?? { bg: "#f3f4f6", text: "#374151" };
  return (
    <span style={{
      backgroundColor: bg, color: text,
      fontSize: 11, fontWeight: 600, borderRadius: 4,
      padding: "2px 7px", whiteSpace: "nowrap",
    }}>
      {ACTION_LABEL[action] ?? action}
    </span>
  );
}

function EntityBadge({ type }) {
  const colors = {
    ticket:      { bg: "#e0f2fe", text: "#0369a1" },
    project:     { bg: "#f0fdf4", text: "#166534" },
    maintenance: { bg: "#fef3c7", text: "#92400e" },
  };
  const { bg, text } = colors[type] ?? { bg: "#f3f4f6", text: "#374151" };
  return (
    <span style={{
      backgroundColor: bg, color: text,
      fontSize: 11, fontWeight: 600, borderRadius: 4,
      padding: "2px 7px",
    }}>
      {ENTITY_ICON[type]} {ENTITY_LABEL[type] ?? type}
    </span>
  );
}

function StatusChip({ label }) {
  return (
    <span style={{
      backgroundColor: "#f1f5f9", color: "#475569", fontSize: 12,
      borderRadius: 4, padding: "1px 6px", fontFamily: "monospace",
    }}>
      {label}
    </span>
  );
}

function DeletedSnapshot({ details }) {
  let parsed = null;
  if (details) {
    try { parsed = JSON.parse(details); } catch { parsed = { title: details }; }
  }
  if (!parsed?.title) return null;
  const prefix = parsed.ticket_number ? `#${parsed.ticket_number} — ` : "";
  return (
    <div className="text-sm mb-1" style={{ color: "#9ca3af", textDecoration: "line-through" }}>
      {prefix}{parsed.title}
      {parsed.company_name && (
        <span style={{ textDecoration: "none", marginRight: 8, fontSize: 11, color: "#d1d5db" }}>
          {parsed.company_name}
        </span>
      )}
      {parsed.technician_name && (
        <span style={{ textDecoration: "none", marginRight: 4, fontSize: 11, color: "#d1d5db" }}>
          · {parsed.technician_name}
        </span>
      )}
    </div>
  );
}

export default function ActivityLogPage() {
  const [entityType, setEntityType] = useState("");
  const [userId, setUserId]         = useState("");
  const [companyId, setCompanyId]   = useState("");
  const [limit, setLimit]           = useState(ACTIVITY_LOG_PAGE_SIZE);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn:  () => usersApi.list(),
    staleTime: 300_000,
    refetchOnMount: "always",
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["companies"],
    queryFn:  () => companiesApi.list(),
    staleTime: 300_000,
  });

  const params = { limit, skip: 0 };
  if (entityType) params.entity_type = entityType;
  if (userId)     params.user_id     = userId;
  if (companyId)  params.company_id  = companyId;

  const { data: entries = [], isLoading, isError } = useQuery({
    queryKey: ["activity-log", params],
    queryFn:  () => activityApi.list(params),
    staleTime: 30_000,
    refetchOnMount: "always",
  });

  return (
    <div className="max-w-3xl mx-auto space-y-5" dir="rtl">
      <h1 className="text-2xl font-bold text-gray-800">יומן פעולות</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">הכל</option>
          <option value="ticket">קריאות</option>
          <option value="project">פרויקטים</option>
          <option value="maintenance">תחזוקה</option>
          <option value="agent_permission">הרשאות סוכנים</option>
          <option value="agent_control">בקרת סוכנים</option>
          <option value="technician">טכנאים</option>
          <option value="api_token">טוקני API</option>
          <option value="agent">סוכנים</option>
          <option value="agent_run">ריצות סוכן</option>
          <option value="company">חברות</option>
          <option value="knowledge_article">מאמרי ידע</option>
          <option value="customer_profile">סביבת IT</option>
          <option value="customer_document">מסמכי לקוח</option>
          <option value="scheduled_check">בדיקות מתוזמנות</option>
          <option value="knowledge_gap">פערי ידע</option>
          <option value="security_finding">ממצאי אבטחה</option>
          <option value="tech_discovery">גילויים טכנולוגיים</option>
          <option value="managed_domain">דומיינים מנוהלים</option>
          <option value="contact">אנשי קשר</option>
          <option value="backup">גיבוי</option>
          <option value="agent_memory">זיכרון לקוח</option>
          <option value="email_blocklist">חסימת מיילים</option>
          <option value="factory_task">משימות מפעל AI</option>
          <option value="factory_worker">עובדי מפעל AI</option>
          <option value="provider_onboarding">{ENTITY_LABEL.provider_onboarding}</option>
          <option value="reuse_decision">{ENTITY_LABEL.reuse_decision}</option>
          <option value="skill_admission">{ENTITY_LABEL.skill_admission}</option>
          <option value="skill_evaluation">{ENTITY_LABEL.skill_evaluation}</option>
          <option value="customer_connection_profile">{ENTITY_LABEL.customer_connection_profile}</option>
          <option value="pilot_feedback">{ENTITY_LABEL.pilot_feedback}</option>
        </select>

        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">כל המשתמשים</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.display_name}</option>
          ))}
        </select>

        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">כל החברות</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {(entityType || userId || companyId) && (
          <button
            onClick={() => { setEntityType(""); setUserId(""); setCompanyId(""); }}
            className="text-sm text-blue-600 hover:underline"
          >
            נקה סינון
          </button>
        )}
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : isError ? (
        <div className="text-center py-16 text-red-500">שגיאה בטעינת הנתונים</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-gray-400">אין פעילויות להצגה</div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div style={{
            position: "absolute", right: 19, top: 8, bottom: 8, width: 2,
            backgroundColor: "#e5e7eb", zIndex: 0,
          }} />

          <div className="space-y-0">
            {entries.map((entry) => (
              <div key={entry.id} className="relative flex gap-4 pb-4">
                {/* Dot */}
                <div style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  backgroundColor: ACTION_COLOR[entry.action]?.text ?? "#9ca3af",
                  border: "2px solid white",
                  boxShadow: "0 0 0 2px #e5e7eb",
                  marginTop: 18, zIndex: 1,
                  position: "relative",
                }} />

                {/* Card */}
                <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
                  {/* Top row */}
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {entry.user_id == null ? (
                      <>
                        <span className="font-semibold text-gray-500 text-sm">⚙️ המערכת</span>
                        <span style={{
                          backgroundColor: "#f0f9ff", color: "#0369a1",
                          fontSize: 11, fontWeight: 600, borderRadius: 4,
                          padding: "2px 7px", whiteSpace: "nowrap",
                        }}>
                          {SYSTEM_ACTION_LABEL[`${entry.entity_type}:${entry.action}`] ?? ACTION_LABEL[entry.action] ?? entry.action}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-gray-800 text-sm">
                          {entry.user_display_name}
                        </span>
                        <ActionBadge action={entry.action} />
                      </>
                    )}
                    <EntityBadge type={entry.entity_type} />
                    <span className="text-xs text-gray-400 mr-auto">{timeAgo(entry.created_at)}</span>
                  </div>

                  {/* Entity link or deleted snapshot */}
                  {entry.action === "deleted" ? (
                    <DeletedSnapshot details={entry.details} />
                  ) : entry.entity_title ? (
                    <div className="text-sm text-gray-700 mb-1">
                      {ENTITY_LINK[entry.entity_type] ? (
                        <Link
                          to={`${ENTITY_LINK[entry.entity_type]}/${entry.entity_id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {entry.entity_title}
                        </Link>
                      ) : (
                        <span className="font-medium">{entry.entity_title}</span>
                      )}
                    </div>
                  ) : null}

                  {/* Details / old→new */}
                  {(entry.old_value || entry.new_value) && (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {/* which setting/field changed: "true ← false" alone does not say (flag toggles, alert config) */}
                      {entry.field_name && <span className="text-xs text-gray-500 font-mono" dir="ltr">{entry.field_name}</span>}
                      {entry.old_value && <StatusChip label={entry.old_value} />}
                      {entry.old_value && entry.new_value && (
                        <span className="text-gray-400 text-xs">←</span>
                      )}
                      {entry.new_value && <StatusChip label={entry.new_value} />}
                    </div>
                  )}
                  {entry.details && ((!entry.old_value && !entry.new_value) || entry.entity_type !== "ticket") && entry.action !== "deleted" && (
                    <p className="text-xs text-gray-500 mt-1 truncate">{entry.details}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Load more */}
          {canLoadMoreActivity(entries.length, limit) && (
            <div className="text-center pt-2">
              <button
                onClick={() => setLimit(nextActivityLogLimit)}
                className="text-sm text-blue-600 hover:underline px-4 py-2"
              >
                טען עוד
              </button>
            </div>
          )}
          {hitActivityLogCeiling(entries.length, limit) && (
            <p className="text-center text-xs text-gray-400 pt-2">
              מוצגות {entries.length} הרשומות האחרונות. להצגת רשומות ישנות יותר יש להשתמש במסננים.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
